import React, { useState, useEffect, useMemo } from "react";
import { Download, Copy, Check } from "lucide-react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetFooter,
  Button,
  Badge,
  DataTable,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
  SpinnerLoader,
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from "lift-ui";
import type { TableColumn } from "lift-ui";
import {
  getRunLogById,
  getRunLogErrors,
  RunLogWithEntries,
  RunLogErrors,
  RunType,
} from "../../../adverity-logs/api";

interface RunLogsDialogProps {
  open: boolean;
  onClose: () => void;
  runId: string | null;
  mode?: "full" | "errors";
}

interface ParsedLogEntry {
  timestamp: string;
  level: string;
  message: string;
  raw_message?: string;
  logger_name?: string;
}

type LogRow = ParsedLogEntry & { _srNo: number };

const RUN_TYPE_LABELS: Record<RunType, string> = {
  authorization_monitor: "Authorization Monitor",
  datastream_creation: "Datastream Creation",
  backup_fetch: "Backup Fetch",
  datastream_update: "Datastream Update",
  authorization_sync: "Authorization Sync",
};

const getLevelClasses = (level: string): string => {
  switch (level.toUpperCase()) {
    case "ERROR":
      return "bg-red-100 text-red-700 border border-red-300";
    case "WARNING":
    case "WARN":
      return "bg-amber-100 text-amber-700 border border-amber-300";
    case "INFO":
      return "bg-green-100 text-green-700 border border-green-300";
    case "DEBUG":
      return "bg-gray-100 text-gray-600 border border-gray-300";
    case "SUCCESS":
      return "bg-emerald-100 text-emerald-700 border border-emerald-300";
    default:
      return "bg-gray-100 text-gray-600 border border-gray-300";
  }
};

const RunLogsDialog: React.FC<RunLogsDialogProps> = ({
  open,
  onClose,
  runId,
  mode: initialMode = "full",
}) => {
  const [runLog, setRunLog] = useState<RunLogWithEntries | null>(null);
  const [errorLog, setErrorLog] = useState<RunLogErrors | null>(null);
  const [loading, setLoading] = useState(false);
  const [mode, setMode] = useState<"full" | "errors">(initialMode);
  const [parsedLogs, setParsedLogs] = useState<ParsedLogEntry[]>([]);
  const [copySuccess, setCopySuccess] = useState(false);

  useEffect(() => {
    setMode(initialMode);
  }, [initialMode]);

  useEffect(() => {
    if (open && runId) {
      fetchData();
    }
  }, [open, runId, mode]);

  const fetchData = async () => {
    if (!runId) return;
    setLoading(true);
    try {
      if (mode === "full") {
        const data = await getRunLogById(runId);
        setRunLog(data);
        setErrorLog(null);
        parseLogs(data.log_entries);
      } else {
        const data = await getRunLogErrors(runId);
        setErrorLog(data);
        setRunLog(null);
        parseErrorLogs(data.errors);
      }
    } catch (err) {
      console.error("Error fetching run log", err);
    } finally {
      setLoading(false);
    }
  };

  const parseLogs = (logEntries: string | null) => {
    if (!logEntries) {
      setParsedLogs([]);
      return;
    }

    try {
      const parsed = JSON.parse(logEntries);
      if (Array.isArray(parsed)) {
        setParsedLogs(
          parsed.map((entry: any) => ({
            timestamp: entry.timestamp || "",
            level: entry.level || "INFO",
            message: entry.message || entry.raw_message || "",
            raw_message: entry.raw_message,
            logger_name: entry.logger_name,
          }))
        );
        return;
      }
    } catch {
      // Not JSON, parse as plain text
    }

    const lines = logEntries.split("\n").filter((line) => line.trim());
    const parsed: ParsedLogEntry[] = lines.map((line) => {
      const match = line.match(/^(\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2},?\d*)\s*-\s*(\w+)\s*-\s*(.*)$/);
      if (match) {
        return { timestamp: match[1], level: match[2], message: match[3], raw_message: match[3] };
      }
      return { timestamp: "", level: "INFO", message: line, raw_message: line };
    });
    setParsedLogs(parsed);
  };

  const parseErrorLogs = (errors: string) => {
    if (!errors) {
      setParsedLogs([]);
      return;
    }

    const lines = errors.split("\n").filter((line) => line.trim());
    const parsed: ParsedLogEntry[] = lines.map((line) => {
      const match = line.match(/^(\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2},?\d*)\s*-\s*(\w+)\s*-\s*(.*)$/);
      if (match) {
        return { timestamp: match[1], level: match[2], message: match[3], raw_message: match[3] };
      }
      return { timestamp: "", level: "ERROR", message: line, raw_message: line };
    });
    setParsedLogs(parsed);
  };

  const formatTime = (timestamp: string): string => {
    if (!timestamp) return "--:--:--";
    try {
      const normalized = timestamp.replace(",", ".").replace("T", " ");
      const date = new Date(normalized);
      if (isNaN(date.getTime())) {
        const timeMatch = timestamp.match(/(\d{2}:\d{2}:\d{2})/);
        return timeMatch ? timeMatch[1] : timestamp.substring(11, 19);
      }
      return date.toLocaleTimeString("en-US", {
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: false,
      });
    } catch {
      return timestamp.substring(11, 19) || "--:--:--";
    }
  };

  const getStatusClasses = (status: string): string => {
    switch (status) {
      case "running": return "bg-amber-100 text-amber-700 border-amber-400";
      case "completed": return "bg-green-100 text-green-700 border-green-400";
      case "failed": return "bg-red-100 text-red-700 border-red-400";
      default: return "bg-gray-100 text-gray-600";
    }
  };

  const handleDownload = () => {
    const logText = parsedLogs
      .map((log) => `[${log.timestamp}] [${log.level}] ${log.raw_message || log.message}`)
      .join("\n");
    const blob = new Blob([logText], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `run-log-${runId}-${mode}-${new Date().toISOString().split("T")[0]}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleCopy = async () => {
    const logText = parsedLogs
      .map((log) => `[${log.timestamp}] [${log.level}] ${log.raw_message || log.message}`)
      .join("\n");
    try {
      await navigator.clipboard.writeText(logText);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    } catch (err) {
      console.error("Failed to copy", err);
    }
  };

  const tableData = useMemo<LogRow[]>(
    () => parsedLogs.map((log, i) => ({ ...log, _srNo: i + 1 })),
    [parsedLogs]
  );

  const columns = useMemo((): TableColumn<LogRow>[] => [
    {
      key: "_srNo",
      header: "Sr No",
      accessor: (row) => row._srNo,
      width: "70px",
      render: (_, row) => <span className="text-gray-500">{row._srNo}</span>,
    },
    {
      key: "time",
      header: "Time",
      accessor: (row) => row.timestamp,
      width: "100px",
      render: (_, row) => <span className="text-gray-600 font-mono text-xs">{formatTime(row.timestamp)}</span>,
    },
    {
      key: "level",
      header: "Level",
      accessor: (row) => row.level,
      width: "90px",
      align: "center" as const,
      render: (_, row) => (
        <span className={`inline-block px-2.5 py-0.5 rounded-full text-[11px] font-medium uppercase whitespace-nowrap ${getLevelClasses(row.level)}`}>
          {row.level}
        </span>
      ),
    },
    {
      key: "message",
      header: "Message",
      accessor: (row) => row.raw_message || row.message,
      render: (_, row) => (
        <span className="text-gray-700 text-xs break-words whitespace-pre-wrap">
          {row.raw_message || row.message}
        </span>
      ),
    },
  ], []);

  const currentData = mode === "full" ? runLog : errorLog;

  const logsDataTable = (
    <div className="flex-1 flex flex-col min-h-0">
      <DataTable<LogRow>
        className="flex-1 min-h-0 overflow-auto"
        maxHeight="100%"
        columns={columns}
        data={tableData}
        loading={loading}
        emptyMessage={mode === "errors" ? "No errors found in this run." : "No log entries available."}
        stickyHeader
        stickyFooter
        showActions={false}
      />
    </div>
  );

  return (
    <Sheet open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <SheetContent
        side="right"
        className="w-[900px] sm:max-w-[900px] flex flex-col h-full overflow-y-auto"
      >
        <SheetHeader>
          <div className="flex items-center justify-between">
            <SheetTitle>Run Logs</SheetTitle>
            <div className="flex items-center gap-2">
              {currentData && (
                <>
                  <Badge variant="outline" className="text-xs">
                    {RUN_TYPE_LABELS[currentData.run_type] || currentData.run_type}
                  </Badge>
                  <span className={`inline-block px-2.5 py-0.5 rounded-full text-xs font-medium capitalize border ${getStatusClasses(currentData.status)}`}>
                    {currentData.status}
                  </span>
                </>
              )}
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button variant="outline" size="icon" onClick={handleCopy} disabled={parsedLogs.length === 0} className="h-8 w-8">
                      {copySuccess ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent><p>{copySuccess ? "Copied!" : "Copy to Clipboard"}</p></TooltipContent>
                </Tooltip>
              </TooltipProvider>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button variant="outline" size="icon" onClick={handleDownload} disabled={parsedLogs.length === 0} className="h-8 w-8">
                      <Download className="h-4 w-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent><p>Download Logs</p></TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
          </div>
        </SheetHeader>

        <Tabs
          value={mode}
          onValueChange={(v) => setMode(v as "full" | "errors")}
          className="flex flex-1 flex-col min-h-0 mt-4"
        >
          <TabsList className="grid grid-cols-2 w-fit mb-4">
            <TabsTrigger value="full">Full Logs</TabsTrigger>
            <TabsTrigger value="errors">Errors Only</TabsTrigger>
          </TabsList>
          <TabsContent value="full" className="flex-1 flex flex-col min-h-0 mt-0 data-[state=inactive]:hidden">
            {logsDataTable}
          </TabsContent>
          <TabsContent value="errors" className="flex-1 flex flex-col min-h-0 mt-0 data-[state=inactive]:hidden">
            {errorLog && parsedLogs.length > 0 && (
              <div className="px-3 py-2 bg-red-50 border border-red-200 rounded-md text-red-800 text-sm mb-3 flex-shrink-0">
                Found <strong>{errorLog.error_count}</strong> error/warning entries
              </div>
            )}
            {logsDataTable}
          </TabsContent>
        </Tabs>

        <SheetFooter className="border-t pt-4 mt-4 flex-shrink-0">
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
};

export default RunLogsDialog;
